import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Printer, FileDown, Eye, AlertTriangle, Loader2, Trash2, Lock, ArrowLeft,
} from "lucide-react";
import { useConfirm } from "@/components/ConfirmProvider";
import ClearableSelect from "@/components/ClearableSelect";
import NumberInput from "@/components/NumberInput";
import FlagCheck from "@/components/FlagCheck";

export const MODULI = {
  mandato_conto_terzi: { label: "Contratto di intermediazione", ds: "Incarico a vendere in conto terzi", owner: "vehicle" },
  presa_carico: { label: "Dichiarazione di presa in carico", ds: "Ricevuta del ritiro del veicolo", owner: "vehicle" },
  contratto_acquisto_privato: { label: "Contratto di acquisto da privato", ds: "Acquisto del veicolo in conto proprio", owner: "vehicle" },
  preventivo: { label: "Preventivo", ds: "Senza firme, con validità", owner: "preventivo" },
  proposta_vendita: { label: "Proposta di vendita", ds: "In attesa di accettazione", owner: "pratica" },
  contratto_vendita: { label: "Contratto di vendita", ds: "Documento definitivo", owner: "pratica" },
  allegato_privacy: { label: "Informativa privacy", ds: "Stampabile anche da sola", owner: "pratica_o_preventivo" },
  allegato_corretta_informazione: { label: "Corretta informazione acquirente", ds: "Solo in conto terzi", owner: "pratica" },
  dichiarazione_responsabilita: { label: "Dichiarazione di responsabilità", ds: "Fino al passaggio di proprietà", owner: "pratica" },
  consegna_documenti: { label: "Consegna di documenti e accessori", ds: "Ricevuta di ciò che è stato consegnato", owner: "pratica" },
  attestato_ritiro: { label: "Attestato di ritiro del documento", ds: "Se il libretto resta in agenzia", owner: "pratica" },
  dichiarazione_sostitutiva: { label: "Dichiarazione sostitutiva", ds: "Autocertificazione per il PRA", owner: "pratica" },
};

const GRUPPI = [
  { key: "acquisizione", label: "ACQUISIZIONE", color: "#B45309", bg: "#FEF6EC",
    moduli: ["mandato_conto_terzi", "presa_carico", "contratto_acquisto_privato"] },
  { key: "vendita", label: "VENDITA", color: "#0F4C81", bg: "#EDF4FA",
    moduli: ["preventivo", "proposta_vendita", "contratto_vendita", "allegato_privacy", "allegato_corretta_informazione"] },
  { key: "consegna", label: "CONSEGNA", color: "#0E6B45", bg: "#EAF4EF",
    moduli: ["dichiarazione_responsabilita", "consegna_documenti", "attestato_ritiro", "dichiarazione_sostitutiva"] },
];

const CT = ["conto_terzi", "conto_terzi_iva"];
const COMMERCIALI = ["preventivo", "proposta_vendita", "contratto_vendita"];
const CONSEGNA = GRUPPI[2].moduli;
const L_CARICO = { label: "Carico e importi", tab: "importi" };
const L_RITIRO = { label: "Carico e importi → Dati del ritiro", tab: "importi", target: "card-dati-ritiro" };
const L_VENDITA = { label: "Scheda Vendita", tab: "vendita" };
const L_PREV = { label: "Scheda Preventivi", tab: "preventivi" };
const L_CONSEGNE = { label: "Scheda Vendita → Modulo consegne", tab: "vendita", target: "card-dati-consegna" };
const L_COMPENSI = { label: "Carico e importi → Compensi e dettagli intermediazione", tab: "importi", target: "card-compensi-intermediazione" };
const no = (reason, link) => ({ ok: false, reason, link });
// Sprint 3 — sezione indicata dal backend nei rifiuti → stesso link dei moduli grigi.
const SEZIONI = { carico: L_CARICO, ritiro: L_RITIRO, vendita: L_VENDITA, preventivi: L_PREV, consegne: L_CONSEGNE };

// Disponibilità di ciascun modulo con ragione e link al punto dove compilare il dato mancante.
export function statoModulo(m, vehicle, pratica, preventivo) {
  const tc = vehicle?.tipo_carico;
  const dc = pratica?.dati_consegna || {};
  if (m === "mandato_conto_terzi") return CT.includes(tc) ? { ok: true } : no("Solo per veicoli in conto terzi", L_CARICO);
  if (m === "presa_carico") return vehicle?.dati_ritiro?.motivo ? { ok: true } : no("Richiede il motivo del ritiro", L_RITIRO);
  if (m === "contratto_acquisto_privato") {
    if (tc === "conto_proprio_art36") return { ok: true };
    if (tc === "conto_proprio_iva") return no("In IVA esposta il documento che fa fede è la fattura del venditore");
    return no("Solo per veicoli in conto proprio art. 36", L_CARICO);
  }
  if (COMMERCIALI.includes(m) && !tc) return no("Tipo di carico mancante: regime IVA non determinabile", L_CARICO);
  if (m === "preventivo") return preventivo ? { ok: true } : no("Richiede un preventivo", L_PREV);
  if (m === "allegato_privacy") return pratica || preventivo ? { ok: true } : no("Richiede un preventivo o una pratica", L_PREV);
  if (!pratica) return no(m === "contratto_vendita" ? "Richiede una pratica confermata" : "Richiede una pratica", L_VENDITA);
  if (m === "contratto_vendita" && pratica.status === "draft") return no("Richiede una pratica confermata: la pratica è in bozza", L_VENDITA);
  if (m === "allegato_corretta_informazione" && !CT.includes(tc)) return no("Solo in conto terzi", L_CARICO);
  if (CONSEGNA.includes(m)) {
    if (!dc.data_ora_consegna) return no("Richiede la data di consegna", L_CONSEGNE);
    if (m === "attestato_ritiro" && !dc.documento_trattenuto) return no("Richiede il documento di circolazione trattenuto", L_CONSEGNE);
  }
  return { ok: true };
}

function ModCard({ m, stato, gruppo, selected, onSelect, onGoTo }) {
  if (!stato.ok) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 flex flex-col gap-1 cursor-default"
           aria-disabled="true" data-testid={`stampa-modulo-${m}-disabled`}>
        <span className="text-[13px] font-medium text-slate-400 leading-tight">{MODULI[m].label}</span>
        <span className="text-[11px] text-slate-600 leading-snug flex gap-1.5 items-start" data-testid={`stampa-modulo-${m}-reason`}>
          <Lock className="h-3 w-3 mt-0.5 shrink-0 text-slate-400" />
          <span>
            {stato.reason}
            {stato.link && <> — <button type="button" className="text-[#0F4C81] underline underline-offset-2 hover:text-blue-900"
              onClick={() => onGoTo?.(stato.link.tab, stato.link.target)} data-testid={`stampa-modulo-${m}-link`}>{stato.link.label}</button></>}
          </span>
        </span>
      </div>
    );
  }
  return (
    <button type="button" onClick={() => onSelect(m)} data-testid={`stampa-modulo-${m}`}
            style={selected ? { borderColor: gruppo.color, background: gruppo.bg } : undefined}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = gruppo.color; e.currentTarget.style.background = gruppo.bg; }}
            onMouseLeave={(e) => { if (!selected) { e.currentTarget.style.borderColor = ""; e.currentTarget.style.background = ""; } }}
            className={`text-left rounded-md border bg-white px-3 py-2 flex flex-col gap-0.5 transition-colors duration-150 ${selected ? "border-2" : "border-slate-200"}`}>
      <span className="text-[13px] font-semibold leading-tight text-slate-900">{MODULI[m].label}</span>
      <span className="text-[11px] text-slate-500 leading-snug">{MODULI[m].ds}</span>
    </button>
  );
}

function Schermata1({ stati, modulo, onSelect, onGoTo }) {
  return (
    <div className="space-y-4" data-testid="stampa-elenco-moduli">
      {GRUPPI.map((g) => (
        <div key={g.key} data-testid={`stampa-gruppo-${g.key}`}>
          <h3 className="flex items-center gap-2 mb-2 text-[10.5px] font-bold tracking-[.07em]" style={{ color: g.color }}>
            <i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: g.color }} />{g.label}
          </h3>
          <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))" }}>
            {g.moduli.map((m) => (
              <ModCard key={m} m={m} stato={stati[m]} gruppo={g} selected={modulo === m} onSelect={onSelect} onGoTo={onGoTo} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function PBox({ title, children, testid }) {
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
      <h4 className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10.5px] font-bold tracking-wide text-slate-600">{title}</h4>
      <div className="p-3 flex flex-wrap gap-x-4 gap-y-3">{children}</div>
    </div>
  );
}

export function Fld({ label, hint, children, full, dim }) {
  return (
    <div className={`${full ? "basis-full" : "flex-1 basis-[210px]"} min-w-0 ${dim ? "opacity-50" : ""}`}>
      <Label className="text-[11px] text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[10.5px] text-slate-500 mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

export function Sw({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex-1 basis-[210px]">
      <FlagCheck label={label} checked={checked} onChange={onChange} testId={testid} />
      {hint && <p className="text-[11px] text-slate-500 ml-6 mt-0.5">{hint}</p>}
    </div>
  );
}

// Sprint 12 — valori economici del mandato: decisi dal server (veicolo o azienda), qui solo mostrati
const ORIGINE = { veicolo: "dal veicolo", azienda: "dall'azienda" };
function ValoreMandato({ label, val, testid, link, onGoTo, fmt = (x) => fmtEur(x), vuoto }) {
  return (
    <div className="flex-1 basis-[210px] min-w-0" data-testid={testid}>
      <Label className="text-[11px] text-slate-500">{label}</Label>
      <div className="mt-1 h-9 px-3 flex items-center rounded-md border border-slate-200 bg-slate-50 text-sm" data-testid={`${testid}-valore`}>
        {val?.valore != null ? fmt(val.valore) : <span className="text-slate-400 italic">{vuoto}</span>}
      </div>
      <p className="text-[10.5px] text-slate-500 mt-1 leading-snug" data-testid={`${testid}-origine`}>
        {val?.origine ? ORIGINE[val.origine] : "non impostato"}<LinkSezione link={link} onGoTo={onGoTo} testid={`${testid}-link`} />
      </p>
    </div>
  );
}

const vaiA = (link, onGoTo) => (link.href ? window.location.assign(link.href) : onGoTo?.(link.tab, link.target, link.campo));

function LinkSezione({ link, onGoTo, testid }) {
  if (!link) return null;
  if (link.href) return <> — <a href={link.href} className="text-[#0F4C81] underline underline-offset-2" data-testid={testid}>{link.label}</a></>;
  return <> — <button type="button" className="text-[#0F4C81] underline underline-offset-2 hover:text-blue-900"
    onClick={() => onGoTo?.(link.tab, link.target, link.campo)} data-testid={testid}>{link.label}</button></>;
}

// Flag di un allegato: disponibilità da statoModulo, come i moduli della prima schermata.
function AllegatoSw({ label, hint, stato, checked, onChange, testid, onGoTo }) {
  if (stato.ok) return <Sw label={label} hint={hint} checked={checked} onChange={onChange} testid={testid} />;
  return (
    <div className="flex-1 basis-[210px] opacity-90" data-testid={`${testid}-disabled`}>
      <div className="text-slate-400"><FlagCheck label={label} checked={false} onChange={() => {}} testId={testid} disabled /></div>
      <p className="text-[11px] text-slate-600 ml-6 mt-0.5 flex gap-1.5 items-start" data-testid={`${testid}-reason`}>
        <Lock className="h-3 w-3 mt-0.5 shrink-0 text-slate-400" />
        <span>{stato.reason}<LinkSezione link={stato.link} onGoTo={onGoTo} testid={`${testid}-link`} /></span>
      </p>
    </div>
  );
}

function Schermata2({ modulo, params, setParams, meta, sitesList, stati, onGoTo }) {
  const set = (k) => (v) => setParams((p) => ({ ...p, [k]: v }));
  const par = params.parametri || {};
  const setPar = (k) => (v) => setParams((p) => ({ ...p, parametri: { ...(p.parametri || {}), [k]: v } }));
  const comm = COMMERCIALI.includes(modulo);
  const contr = ["proposta_vendita", "contratto_vendita"].includes(modulo);
  const conConsulente = comm || CONSEGNA.includes(modulo);
  const boxBlocchi = comm || modulo === "presa_carico";
  const altri = comm || contr || ["mandato_conto_terzi", "presa_carico", "contratto_acquisto_privato"].includes(modulo);
  const vm = meta.valori_mandato || {};
  const lAz = meta.company_id ? { href: `/companies/${meta.company_id}`, label: "Impostazioni azienda" } : null;
  return (
    <div className="space-y-3" data-testid="stampa-maschera">
      <p className="text-[11.5px] text-slate-500">Le scelte si salvano su questo veicolo per il modulo selezionato e si ripresentano identiche alla prossima apertura.</p>
      <PBox title="DOCUMENTO" testid="stampa-box-documento">
        <Fld label={<>Sede <span className="text-red-600">*</span></>} hint="Stampata in intestazione come sede operativa e come luogo di stipula sopra le firme.">
          {sitesList.length === 0 ? (
            <p className="text-xs text-amber-700" data-testid="stampa-no-sedi">Nessuna sede configurata: creala in Azienda → Sedi Operative.</p>
          ) : (
            <ClearableSelect value={params.sede_id || ""} onChange={(v) => set("sede_id")(v || "")} clearable={false}
              options={sitesList.map((s) => [s.id, s.nome])} placeholder="Seleziona una sede..." testId="stampa-sede-select" />
          )}
        </Fld>
        {comm && meta.conti.length > 0 && (
          <Fld label="Conto corrente per il bonifico">
            <ClearableSelect value={params.conto_iban || ""} onChange={(v) => set("conto_iban")(v || "")} clearable={false}
              options={meta.conti.map((c) => [c.iban, `${c.banca || "Conto"} — ${c.iban}${c.predefinito ? " (predefinito)" : ""}`])}
              testId="stampa-conto-select" />
          </Fld>
        )}
        {conConsulente && (
          <Fld label="Consulente alla vendita" hint="Dal venditore della pratica — non modificabile qui.">
            <Input value={meta.consulente || "—"} disabled className="h-9 bg-slate-50" data-testid="stampa-consulente" />
          </Fld>
        )}
      </PBox>

      {modulo === "preventivo" && (
        <PBox title="VALIDITÀ DEL PREVENTIVO" testid="stampa-box-validita">
          <Fld label="Validità">
            <ClearableSelect value={params.validita_tipo || "giorni"} onChange={(v) => set("validita_tipo")(v || "giorni")} clearable={false}
              options={[["giorni", "Giorni dalla data di emissione"], ["fine_mese", "Fino a fine mese"], ["libero", "Testo libero"]]}
              testId="stampa-validita-tipo" />
          </Fld>
          {(params.validita_tipo || "giorni") === "giorni" && (
            <Fld label="Giorni"><NumberInput value={params.validita_giorni} onChange={set("validita_giorni")} testId="stampa-validita-giorni" /></Fld>
          )}
          {params.validita_tipo === "libero" && (
            <Fld label="Testo della validità" full>
              <Input value={params.validita_testo || ""} onChange={(e) => set("validita_testo")(e.target.value)} className="h-9" data-testid="stampa-validita-testo" />
            </Fld>
          )}
          <Sw label="Salvo disponibilità del veicolo" hint="Aggiunge la riserva alla riga di validità."
              checked={params.salvo_disponibilita} onChange={set("salvo_disponibilita")} testid="stampa-salvo-disponibilita" />
        </PBox>
      )}

      {modulo === "mandato_conto_terzi" && (
        <PBox title="INCARICO" testid="stampa-box-incarico">
          <Sw label="Deposito e custodia (art. 3)" hint="Il veicolo è affidato in deposito all'agenzia."
              checked={params.deposito_attivo} onChange={set("deposito_attivo")} testid="stampa-deposito-switch" />
          <AllegatoSw label="Allega la dichiarazione di presa in carico" hint="Richiede il motivo del ritiro sulla scheda veicolo."
              stato={stati.presa_carico} onGoTo={onGoTo}
              checked={params.presa_carico} onChange={set("presa_carico")} testid="stampa-presa-carico-switch" />
        </PBox>
      )}

      {modulo === "mandato_conto_terzi" && (
        <PBox title={<>PARAMETRI DEL MANDATO <span className="font-normal normal-case tracking-normal">— si stampano questi valori; si modificano nella sezione indicata</span></>} testid="stampa-box-parametri-mandato">
          <ValoreMandato label="Penale per revoca anticipata (art. 2)" val={vm.penale_revoca} vuoto="nessuna penale: il comma non si stampa"
            link={L_COMPENSI} onGoTo={onGoTo} testid="stampa-mandato-penale" />
          <ValoreMandato label="Compenso per vendita diretta (art. 4.6)" val={vm.vendita_diretta} vuoto="non impostato"
            link={L_COMPENSI} onGoTo={onGoTo} testid="stampa-mandato-vendita-diretta" />
          <ValoreMandato label="Costo del deposito (art. 3.4)" val={vm.costo_deposito} vuoto="deposito gratuito"
            fmt={(x) => `${fmtEur(x)}${vm.costo_deposito?.periodo ? ` ${vm.costo_deposito.periodo}` : ""}`}
            link={L_COMPENSI} onGoTo={onGoTo} testid="stampa-mandato-costo-deposito" />
          <ValoreMandato label="Termine di disdetta (art. 2.2)" val={vm.giorni_disdetta} vuoto="mancante: la stampa si blocca"
            fmt={(x) => `${x} giorni`} link={lAz} onGoTo={onGoTo} testid="stampa-mandato-disdetta" />
        </PBox>
      )}

      {modulo === "contratto_acquisto_privato" && (
        <PBox title="ALLEGATI" testid="stampa-box-allegati-acquisto">
          <Sw label="Stato d'uso allegato" hint="Spunta se alleghi su carta la dichiarazione di stato d'uso: l'art. 3.5 la richiama."
              checked={params.stato_uso_allegato} onChange={set("stato_uso_allegato")} testid="stampa-stato-uso-switch" />
          <AllegatoSw label="Allega la dichiarazione di presa in carico" hint="L'art. 3.1 richiama le dotazioni indicate in quel documento."
              stato={stati.presa_carico} onGoTo={onGoTo}
              checked={params.presa_carico} onChange={set("presa_carico")} testid="stampa-presa-carico-switch" />
        </PBox>
      )}

      {boxBlocchi && (
        <PBox title="BLOCCHI DA STAMPARE" testid="stampa-box-blocchi">
          {contr && <Sw label="Condizioni generali di vendita" checked={params.blocco_condizioni} onChange={set("blocco_condizioni")} testid="stampa-blocco-condizioni" />}
          {comm && <AllegatoSw label="Informativa privacy" stato={stati.allegato_privacy} onGoTo={onGoTo} checked={params.allega_privacy} onChange={set("allega_privacy")} testid="stampa-allega-privacy-switch" />}
          {contr && <AllegatoSw label="Dichiarazione di corretta informazione" stato={stati.allegato_corretta_informazione} onGoTo={onGoTo} checked={params.allega_corretta_informazione} onChange={set("allega_corretta_informazione")} testid="stampa-allega-corretta-switch" />}
          {comm && meta.conti.length > 0 && <Sw label="Dati per il bonifico" checked={params.blocco_bonifico} onChange={set("blocco_bonifico")} testid="stampa-blocco-bonifico" />}
          {comm && <Sw label="Note della pratica" checked={params.blocco_note} onChange={set("blocco_note")} testid="stampa-blocco-note" />}
          {modulo === "presa_carico" && <Sw label="Dotazioni e accessori consegnati" hint="Dalle spunte della scheda Accessori e dotazioni."
              checked={params.blocco_dotazioni} onChange={set("blocco_dotazioni")} testid="stampa-blocco-dotazioni" />}
        </PBox>
      )}

      {contr && (
        <PBox title="CLAUSOLE FACOLTATIVE" testid="stampa-box-clausole">
          <Sw label="Stato d'uso allegato" hint="Spunta solo se alleghi su carta la dichiarazione di stato d'uso: l'art. 7.2 la richiama."
              checked={params.stato_uso_allegato} onChange={set("stato_uso_allegato")} testid="stampa-stato-uso-switch" />
          <Sw label="Vendita nello stato di fatto — «vista e piaciuta»" hint="Richiede l'elenco dei vizi riconosciuti."
              checked={params.art7_vista_piaciuta} onChange={set("art7_vista_piaciuta")} testid="stampa-art7-switch" />
          <Sw label="Clausola di vetustà" hint="Per veicoli datati o ad alta percorrenza."
              checked={params.art8_vetusta} onChange={set("art8_vetusta")} testid="stampa-art8-switch" />
          <Fld label="Vizi e interventi riconosciuti, accettati dall'acquirente" full dim={!params.art7_vista_piaciuta}>
            <Input value={par.difetti_dichiarati || ""} disabled={!params.art7_vista_piaciuta} className="h-9"
                   onChange={(e) => setPar("difetti_dichiarati")(e.target.value)} data-testid="stampa-difetti-input" />
          </Fld>
        </PBox>
      )}

      {contr && (
        <PBox title={<>PARAMETRI CONTRATTUALI <span className="font-normal normal-case tracking-normal">— precompilati dalle impostazioni azienda</span></>} testid="stampa-box-parametri">
          <Fld label="Mesi di garanzia legale" hint="Minimo di legge 12.">
            <NumberInput value={par.mesi_garanzia_legale} onChange={setPar("mesi_garanzia_legale")} testId="stampa-garanzia-input" />
          </Fld>
          <Fld label="Recesso nelle sedi fisiche">
            <ClearableSelect value={par.recesso_sedi_fisiche ? "si" : "no"} clearable={false}
              onChange={(v) => setPar("recesso_sedi_fisiche")(v === "si")}
              options={[["no", "Non previsto"], ["si", "Previsto, con termine"]]} testId="stampa-recesso-sede-select" />
          </Fld>
          <Fld label="Giorni di recesso in sede" hint="Si attiva scegliendo «Previsto, con termine»." dim={!par.recesso_sedi_fisiche}>
            <NumberInput value={par.giorni_recesso_sedi} onChange={setPar("giorni_recesso_sedi")} disabled={!par.recesso_sedi_fisiche} testId="stampa-recesso-sede-giorni" />
          </Fld>
          <Fld label="Recesso fuori dalle sedi (giorni)" hint="Minimo di legge 14.">
            <NumberInput value={par.giorni_recesso_fuori_sede} onChange={setPar("giorni_recesso_fuori_sede")} testId="stampa-recesso-input" />
          </Fld>
          <Fld label="Spese di istruttoria €">
            <NumberInput mode="decimal" value={par.spese_istruttoria} onChange={setPar("spese_istruttoria")} testId="stampa-spese-istruttoria" />
          </Fld>
        </PBox>
      )}

      {!altri && (
        <div className="text-[13px] text-slate-600 bg-slate-50 border border-dashed border-slate-200 rounded-md p-3" data-testid="stampa-vuoto">
          Questo documento non ha parametri da impostare oltre alla sede: puoi passare direttamente all'anteprima.
        </div>
      )}
    </div>
  );
}

const PARAM_KEYS = ["mesi_garanzia_legale", "recesso_sedi_fisiche", "giorni_recesso_sedi", "giorni_recesso_fuori_sede", "spese_istruttoria"];

function initParams(saved, meta, sl, modulo) {
  const acq = modulo === "contratto_acquisto_privato";
  let sede = saved.sede_id && sl.some((s) => s.id === saved.sede_id) ? saved.sede_id : "";
  if (!sede && sl.length === 1) sede = sl[0].id;
  const def = (k, v) => (saved[k] !== undefined ? saved[k] : v);
  const pc = meta.defaults || {};
  const parametri = { ...(saved.parametri || {}) };
  PARAM_KEYS.forEach((k) => { if (parametri[k] === undefined || parametri[k] === null) parametri[k] = pc[k] ?? null; });
  const pred = meta.conti.find((c) => c.predefinito) || meta.conti[0];
  return {
    ...saved, sede_id: sede, parametri,
    conto_iban: def("conto_iban", pred?.iban || ""),
    allega_privacy: def("allega_privacy", true), blocco_condizioni: def("blocco_condizioni", true),
    // Sprint 12 — scelte salvate prima di questo sprint (senza presa_carico): lo stato d'uso era un default, non una scelta
    stato_uso_allegato: acq && saved.presa_carico === undefined ? true : def("stato_uso_allegato", acq),
    presa_carico: def("presa_carico", acq),
    allega_corretta_informazione: def("allega_corretta_informazione", modulo === "contratto_vendita"),
    blocco_bonifico: def("blocco_bonifico", true), blocco_note: def("blocco_note", true),
    blocco_dotazioni: def("blocco_dotazioni", true), salvo_disponibilita: def("salvo_disponibilita", true),
    validita_tipo: def("validita_tipo", "giorni"),
    validita_giorni: def("validita_giorni", meta.giorni_validita_preventivo || pc.giorni_validita_proposta || null),
  };
}

async function erroreDa(e) {
  let d = e?.response?.data;
  if (d instanceof Blob) { try { d = await d.text(); } catch { d = null; } }
  if (typeof d === "string") { try { d = JSON.parse(d); } catch { /* testo semplice */ } }
  return { status: e?.response?.status, detail: d?.detail ?? d };
}

function useOrigini(open, praticaId, vehicleId) {
  const [pratica, setPratica] = useState(null);
  const [preventivi, setPreventivi] = useState([]);
  const [prevId, setPrevId] = useState(null);
  const [preventivo, setPreventivo] = useState(null);
  useEffect(() => {
    if (!open) return;
    setPratica(null); setPreventivi([]); setPrevId(null); setPreventivo(null);
    (async () => {
      let pr = null;
      if (praticaId) { try { pr = (await api.get(`/vendite/${praticaId}`)).data; } catch { pr = null; } }
      setPratica(pr);
      try {
        const r = await api.get(`/vendite?vehicle_id=${vehicleId}&tipo=preventivo&page_size=100`);
        const att = (r.data?.items || []).filter((x) => x.status !== "annullato")
          .sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));
        setPreventivi(att);
        const orig = att.find((x) => x.id === pr?.preventivo_origine_id);
        setPrevId((orig || att[0])?.id || null);
      } catch { setPreventivi([]); }
    })();
  }, [open, praticaId, vehicleId]);
  useEffect(() => {
    if (!prevId) { setPreventivo(null); return; }
    api.get(`/vendite/${prevId}`).then((r) => setPreventivo(r.data)).catch(() => setPreventivo(null));
  }, [prevId]);
  return { pratica, preventivo, preventivi, prevId, setPrevId };
}

const fmtData = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
const fmtEur = (n) => (n == null ? "—" : `${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

// Sprint 3 — scelta esplicita del preventivo da stampare fra quelli non annullati del veicolo.
function SceltaPreventivo({ preventivi, prevId, setPrevId, origineId }) {
  return (
    <div className="mb-3 border border-slate-200 rounded-md overflow-hidden" data-testid="stampa-scelta-preventivo">
      <h4 className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10.5px] font-bold tracking-wide text-slate-600">PREVENTIVO DA STAMPARE</h4>
      <table className="w-full text-[12px]">
        <thead className="text-[10.5px] text-slate-500"><tr>
          <th className="w-8" /><th className="text-left px-2 py-1">Codice</th><th className="text-left px-2">Creato il</th>
          <th className="text-left px-2">Ultima modifica</th><th className="text-right px-3">Totale preventivato</th>
        </tr></thead>
        <tbody>
          {preventivi.map((p) => (
            <tr key={p.id} onClick={() => setPrevId(p.id)} data-testid={`stampa-preventivo-row-${p.codice}`}
                className={`cursor-pointer border-t border-slate-100 ${p.id === prevId ? "bg-[#EDF4FA]" : "hover:bg-slate-50"}`}>
              <td className="text-center"><input type="radio" readOnly checked={p.id === prevId} data-testid={`stampa-preventivo-radio-${p.codice}`} /></td>
              <td className="px-2 py-1.5 font-semibold">{p.codice}{p.id === origineId && <span className="ml-1.5 text-[10px] font-normal text-slate-500">(origine della pratica)</span>}</td>
              <td className="px-2">{fmtData(p.created_at)}</td>
              <td className="px-2">{fmtData(p.updated_at)}</td>
              <td className="px-3 text-right font-mono">{fmtEur(p.totale_vendita_finale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Sprint STAMPE-VERSIONI — documenti archiviati (solo quelli esistenti) in cima al passo dei parametri
export function DocumentiArchiviati({ versioni, onApri, onElimina }) {
  if (!versioni.length) return null;
  const data = (d) => (d.created_at || "").slice(0, 10).split("-").reverse().join("/");
  return (
    <div className="mt-3 border border-[#0F4C81]/40 bg-sky-50/60 rounded-md p-3" data-testid="stampa-archiviati">
      <div className="text-sm font-bold text-[#0F4C81]" data-testid="stampa-archiviati-titolo">Documenti archiviati ({versioni.length})</div>
      <ul className="mt-2 space-y-1" data-testid="stampa-versioni-list">
        {versioni.map((d, i) => (
          <li key={d.id} className={`flex items-center justify-between text-sm rounded px-2 py-1 ${i === 0 ? "bg-white border border-slate-200 font-semibold" : "text-slate-600"}`}
              data-testid={i === 0 ? "stampa-archiviato-recente" : `stampa-archiviato-${d.versione}`}>
            <span>Versione {d.versione} — del {data(d)}{i === 0 && <span className="ml-2 text-[10.5px] font-bold uppercase tracking-wide text-[#0F4C81]">più recente</span>}</span>
            <span className="flex items-center gap-1">
              <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onApri(d.id)} data-testid={`stampa-apri-${d.versione}`}><FileDown className="h-4 w-4 mr-1" /> Apri</Button>
              <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => onElimina(d)} data-testid={`stampa-elimina-${d.versione}`}><Trash2 className="h-4 w-4" /></Button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function StampaMandatoDialog({ open, onOpenChange, vehicle, praticaId, onGoTo }) {
  const confirm = useConfirm();
  const { pratica, preventivo, preventivi, prevId, setPrevId } = useOrigini(open, praticaId, vehicle?.id);
  const [modulo, setModulo] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useState({ sede_id: "" });
  const [meta, setMeta] = useState({ conti: [], defaults: {}, consulente: "" });
  const [sitesList, setSitesList] = useState([]);
  const [versioni, setVersioni] = useState([]);
  const [avvisi, setAvvisi] = useState([]);
  const [bloccoLink, setBloccoLink] = useState(null);
  const [avvisoImp, setAvvisoImp] = useState(null);
  const [avvisiDati, setAvvisiDati] = useState([]);

  useEffect(() => { if (open) { setStep(1); setModulo(null); } }, [open]);

  const stati = useMemo(() => Object.fromEntries(
    Object.keys(MODULI).map((m) => [m, statoModulo(m, vehicle, pratica, preventivo)])), [vehicle, pratica, preventivo]);

  const o = modulo ? MODULI[modulo].owner : "vehicle";
  const ownerType = o === "vehicle" ? "vehicle" : "vendita";
  const ownerId = o === "vehicle" ? vehicle?.id : o === "preventivo" ? preventivo?.id
    : o === "pratica" ? pratica?.id : (pratica?.id || preventivo?.id);
  const fonte = o === "preventivo" || (o === "pratica_o_preventivo" && !pratica) ? preventivo : o === "vehicle" ? null : pratica;
  const cid = vehicle?.company_id;
  const usaPreventivo = o === "preventivo" || (o === "pratica_o_preventivo" && !pratica);
  const regPrev = usaPreventivo ? preventivo?.regime_documento : null;

  const refreshVersioni = async () => {
    const docs = await api.get(`/stampe/documenti/${ownerType}/${ownerId}`);
    setVersioni((docs.data?.items || []).filter((d) => d.modulo === modulo));
  };

  useEffect(() => {
    if (!open || step !== 2 || !ownerId || !modulo) return;
    setAvvisi([]); setBloccoLink(null); setAvvisoImp(null);
    (async () => {
      setLoading(true);
      try {
        const [p, docs, sitesRes] = await Promise.all([
          api.get(`/stampe/parametri/${ownerType}/${ownerId}/${modulo}`),
          api.get(`/stampe/documenti/${ownerType}/${ownerId}`),
          cid ? api.get(`/companies/${cid}/sites`) : Promise.resolve({ data: [] }),
        ]);
        const m = { conti: p.data?.conti || [], defaults: p.data?.defaults || {}, consulente: p.data?.consulente || "",
                    giorni_validita_preventivo: p.data?.giorni_validita_preventivo,
                    valori_mandato: p.data?.valori_mandato, company_id: cid };
        const sl = sitesRes.data || [];
        setMeta(m); setSitesList(sl);
        setParams(initParams(p.data?.params || {}, m, sl, modulo));
        setVersioni((docs.data?.items || []).filter((d) => d.modulo === modulo));
      } catch (e) {
        toast.error("Errore nel caricamento della maschera di stampa");
      } finally { setLoading(false); }
    })();
  }, [open, step, ownerType, ownerId, cid, modulo]);

  const canGenerate = !!modulo && !!params.sede_id;
  const saveParams = () => api.put(`/stampe/parametri/${ownerType}/${ownerId}/${modulo}`, { params });

  const handleErr = async (e, fallback) => {
    const { detail } = await erroreDa(e);
    if (detail && typeof detail === "object" && detail.error) {
      const link = detail.link || SEZIONI[detail.sezione] || null;
      setAvvisi(detail.avvisi || [detail.messaggio]); setBloccoLink(link);
      toast.error(detail.messaggio || fallback, link ? {
        action: { label: link.label, onClick: () => vaiA(link, onGoTo) },
      } : undefined);
      return;
    }
    toast.error(typeof detail === "string" ? detail : fallback);
  };

  const openBlob = (data) => {
    const url = URL.createObjectURL(data instanceof Blob ? data : new Blob([data], { type: "application/pdf" }));
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // Sprint RIFINITURE — anteprima e documento definitivo mostrati dentro la maschera
  const [vista, setVista] = useState(null);
  const mostra = (data, tipo, versione) => setVista((old) => {
    if (old?.url) URL.revokeObjectURL(old.url);
    return { url: URL.createObjectURL(data instanceof Blob ? data : new Blob([data], { type: "application/pdf" })), tipo, versione };
  });
  const chiudiVista = () => setVista((old) => { if (old?.url) URL.revokeObjectURL(old.url); return null; });
  useEffect(() => { if (!open || step !== 2) chiudiVista(); }, [open, step, modulo]);

  const run = async (fn, fallback) => {
    if (!canGenerate) { toast.error("Seleziona una sede prima di procedere"); return; }
    setBusy(true); setAvvisi([]); setBloccoLink(null); setAvvisoImp(null); setAvvisiDati([]);
    try { await saveParams(); await fn(); } catch (e) { await handleErr(e, fallback); } finally { setBusy(false); }
  };

  // Un allegato non producibile non parte mai spuntato (il backend comunque lo rifiuterebbe).
  const body = () => ({ owner_type: ownerType, owner_id: ownerId, params: {
    ...params,
    ...(stati.presa_carico.ok ? {} : { presa_carico: false }),
    ...(stati.allegato_privacy.ok ? {} : { allega_privacy: false }),
    ...(stati.allegato_corretta_informazione.ok ? {} : { allega_corretta_informazione: false }),
  } });
  const onPreview = () => run(async () => {
    const r = await api.post(`/stampe/${modulo}/preview`, body(), { responseType: "blob" });
    const h = r.headers?.["x-avviso-impaginazione"];
    if (h) { try { setAvvisoImp(JSON.parse(h)); } catch { setAvvisoImp(null); } }
    const hd = r.headers?.["x-avvisi-dati"];
    if (hd) { try { setAvvisiDati(JSON.parse(hd)); } catch { setAvvisiDati([]); } }
    mostra(r.data, "anteprima");
  }, "Errore durante l'anteprima");
  const onGenera = async () => {
    // Sprint STAMPE-VERSIONI — conferma solo se esistono già documenti archiviati (non cancellati) di questo tipo
    if (versioni.length > 0) {
      const ok = await confirm({
        variant: "approve", title: "Documento già archiviato", confirmLabel: "SI, genera",
        message: `Esistono già ${versioni.length} documenti archiviati di questo tipo. Vuoi generarne uno nuovo?`,
      });
      if (!ok) return;
    }
    await generaOra();
  };
  const generaOra = () => run(async () => {
    const r = await api.post(`/stampe/${modulo}/genera`, body());
    toast.success(`Documento generato (versione ${r.data?.versione})`);
    if (r.data?.avviso_impaginazione) setAvvisoImp(r.data.avviso_impaginazione);
    setAvvisiDati(r.data?.avvisi_dati || []);
    await refreshVersioni();
    // Sprint RIFINITURE — il documento ufficiale si mostra subito, al posto dell'anteprima
    const f = await api.get(`/stampe/documenti/${r.data.id}/file`, { responseType: "blob" });
    mostra(f.data, "definitivo", r.data?.versione);
  }, "Errore durante la generazione del PDF");

  const onApri = async (docId) => {
    try { openBlob((await api.get(`/stampe/documenti/${docId}/file`, { responseType: "blob" })).data); }
    catch { toast.error("Impossibile aprire il documento archiviato"); }
  };

  const onElimina = async (doc) => {
    const ok = await confirm({
      variant: "delete", title: "Elimina documento", confirmLabel: "SI, elimina",
      message: `Eliminare la versione ${doc.versione} di "${MODULI[modulo]?.label}"? L'operazione è definitiva: il documento non sarà più apribile. La numerazione delle versioni resta progressiva.`,
    });
    if (!ok) return;
    try { await api.delete(`/stampe/documenti/${doc.id}`); toast.success("Documento eliminato"); await refreshVersioni(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
  };

  const ultima = versioni[0];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={vista ? "max-w-6xl h-[94vh] h-[94dvh] max-h-[94dvh] !flex flex-col p-0 gap-0 overflow-hidden" : "max-w-4xl max-h-[90vh] overflow-y-auto p-0 gap-0"} data-testid="stampa-mandato-dialog">
        <DialogHeader className="shrink-0 px-5 py-3 border-b flex-row items-center gap-3 space-y-0">
          <DialogTitle className="text-base flex items-center gap-2" data-testid="stampa-dialog-title">
            <Printer className="h-4 w-4" /> {step === 2 && modulo ? `Stampa — ${MODULI[modulo].label}` : "Stampa documento"}
          </DialogTitle>
          <DialogDescription className="ml-auto pr-6 text-right text-[11.5px] leading-snug text-slate-500" data-testid="stampa-contesto">
            Veicolo {vehicle?.codice} — {[vehicle?.marca, vehicle?.modello, vehicle?.targa].filter(Boolean).join(" ")}<br />
            {pratica ? <>Pratica collegata <b data-testid="stampa-pratica-codice">{pratica.codice}</b></> : "Nessuna pratica collegata"}
            {step === 2 && fonte && fonte.tipo === "preventivo" && <><br />Dati dal preventivo <b data-testid="stampa-preventivo-codice">{fonte.codice}</b></>}
          </DialogDescription>
        </DialogHeader>

        <div className="shrink-0 flex items-center gap-2 px-5 py-2 border-b bg-slate-50 text-xs" data-testid="stampa-steps">
          {[["1", "Scelta del documento"], ["2", "Parametri e anteprima"]].map(([n, t], i) => (
            <React.Fragment key={n}>
              {i > 0 && <span className="w-6 h-px bg-slate-300 mx-1" />}
              <span className={`flex items-center gap-1.5 ${String(step) === n ? "text-slate-900 font-semibold" : "text-slate-400"}`}>
                <b className={`w-[19px] h-[19px] rounded-full text-white text-[11px] flex items-center justify-center ${String(step) === n ? "bg-[#0F4C81]" : "bg-slate-300"}`}>{n}</b>{t}
              </span>
            </React.Fragment>
          ))}
        </div>

        {vista ? (
          <div className="flex-1 min-h-0 flex flex-col px-5 py-3 gap-2 overflow-hidden" data-testid="stampa-vista">
            {vista.tipo === "anteprima" ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900 flex items-center gap-1.5" data-testid="stampa-vista-avviso-anteprima">
                <AlertTriangle className="h-4 w-4 shrink-0" /> Anteprima: documento NON registrato. Se è corretto, premi «Genera e archivia il documento» qui sotto: solo così il documento viene numerato e archiviato.
              </div>
            ) : (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-900" data-testid="stampa-vista-definitivo">
                Documento archiviato — versione {vista.versione}. È questo il foglio da stampare.
              </div>
            )}
            {avvisi.length > 0 && (
              <div className="shrink-0 max-h-[25vh] overflow-y-auto rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="stampa-vista-avvisi">
                <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-4 w-4" /> Documento bloccato</div>
                <ul className="list-disc pl-5 mt-1">{avvisi.map((a, i) => <li key={i}>{a}</li>)}</ul>
              </div>
            )}
            <iframe src={vista.url} title={vista.tipo === "anteprima" ? "Anteprima del documento" : "Documento archiviato"}
                    className="flex-1 min-h-[120px] w-full rounded border border-slate-300 bg-slate-100" data-testid="stampa-vista-frame" />
          </div>
        ) : (
        <div className="px-5 py-4" data-testid="stampa-corpo">
          {step === 1 ? (
            <Schermata1 stati={stati} modulo={modulo} onGoTo={onGoTo} onSelect={(m) => { setModulo(m); setStep(2); }} />
          ) : loading ? (
            <p className="text-sm text-slate-500 py-6">Caricamento...</p>
          ) : (
            <>
              {avvisiDati.length > 0 && (
                <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-900" data-testid="stampa-avvisi-dati">
                  <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-4 w-4" /> Dati da completare (il documento è uscito)</div>
                  <ul className="list-disc pl-5 mt-1">{avvisiDati.map((a, i) => (
                    <li key={i} data-testid={`stampa-avviso-dato-${i}`}>{a.messaggio}<LinkSezione link={a.link} onGoTo={onGoTo} testid={`stampa-avviso-dato-link-${i}`} /></li>
                  ))}</ul>
                </div>
              )}
              {avvisoImp && (
                <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-900" data-testid="stampa-avviso-impaginazione">
                  <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-4 w-4" /> Impaginazione oltre le due pagine</div>
                  <p className="mt-1">{avvisoImp.messaggio}</p>
                </div>
              )}
              {avvisi.length > 0 && (
                <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="stampa-avvisi">
                  <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-4 w-4" /> Documento bloccato</div>
                  <ul className="list-disc pl-5 mt-1">{avvisi.map((a, i) => <li key={i}>{a}</li>)}</ul>
                  {bloccoLink && <p className="mt-1 text-[12px]" data-testid="stampa-avvisi-link-wrap">Da compilare<LinkSezione link={bloccoLink} onGoTo={onGoTo} testid="stampa-avvisi-link" /></p>}
                </div>
              )}
              {usaPreventivo && preventivi.length > 0 && (
                <SceltaPreventivo preventivi={preventivi} prevId={prevId} setPrevId={setPrevId} origineId={pratica?.preventivo_origine_id} />
              )}
              {regPrev && regPrev.conto && regPrev.coerente === false && (
                <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-800" data-testid="stampa-avviso-fiscale-preventivo">
                  <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-4 w-4" /> Incoerenza fiscale segnalata — il preventivo si stampa comunque</div>
                  <ul className="list-disc pl-5 mt-1">{(regPrev.avvisi || []).map((a, i) => <li key={i}>{a}</li>)}</ul>
                </div>
              )}
              <Schermata2 modulo={modulo} params={params} setParams={setParams} meta={meta} sitesList={sitesList} stati={stati} onGoTo={onGoTo} />
              <DocumentiArchiviati versioni={versioni} onApri={onApri} onElimina={onElimina} />
            </>
          )}
        </div>
        )}

        <div className={"shrink-0 flex items-center gap-2 px-5 py-3 border-t bg-slate-50"} data-testid="stampa-azioni">
          {vista ? (
            <>
              <Button variant="outline" size="sm" onClick={chiudiVista} className="bg-transparent border-[#0F4C81] text-[#0F4C81] hover:bg-transparent hover:text-[#0c3d68]" data-testid="stampa-vista-indietro-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Torna ai parametri</Button>
              <Button variant="ghost" size="sm" onClick={() => window.open(vista.url, "_blank", "noopener,noreferrer")} data-testid="stampa-vista-scheda-btn"><Eye className="h-4 w-4 mr-1" /> Apri in una nuova scheda</Button>
              {vista.tipo === "anteprima" && (
                <Button onClick={onGenera} disabled={busy} size="lg" className="ml-auto h-12 px-6 text-base bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="stampa-vista-genera-btn">
                  {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Printer className="h-5 w-5 mr-2" />} Genera e archivia il documento
                </Button>
              )}
            </>
          ) : step === 1 ? (
            <span className="text-[11.5px] text-slate-500">Scegli un documento per continuare.</span>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setStep(1)} className="bg-transparent border-[#0F4C81] text-[#0F4C81] hover:bg-transparent hover:text-[#0c3d68]" data-testid="stampa-indietro-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Cambia documento</Button>
              <span className="ml-auto text-[11.5px] text-slate-500" data-testid="stampa-ultima-versione">
                {ultima ? `Ultima versione archiviata: v${ultima.versione} del ${(ultima.created_at || "").slice(0, 10).split("-").reverse().join("/")}` : "Nessuna versione archiviata"}
              </span>
              <Button onClick={onPreview} disabled={busy || loading || !canGenerate} className="bg-[#0F4C81] hover:bg-[#0c3d68] text-white" data-testid="stampa-preview-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />} Anteprima
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
